/**
 * Local-timezone day bucketing for schedule rendering (#4358).
 *
 * Shifts are stored and merged in UTC, but every schedule-facing surface
 * (timesheet, sidebar shift-lists) must display in the user's selected
 * timezone. A 22:00 ET → 02:00 ET (next day) shift contributes 2h to the
 * tester's local Tuesday and 4h to their local Wednesday — the user's
 * mental model is local-day buckets, not UTC ones.
 *
 * These helpers do the projection without changing the underlying merged-
 * schedule contract: the API still emits each span under its UTC-start-date
 * (see `mergeDays` in `tester-schedule.service.ts`); consumers re-bucket at
 * render time by splitting segments at local-tz midnight boundaries.
 *
 * All inputs are ISO 8601 UTC instants; outputs use `YYYY-MM-DD` local-tz
 * date strings (consumed verbatim as labels — no further projection needed).
 */
const PARTS_FORMATTER_CACHE = new Map();
function getPartsFormatter(timeZone) {
    const cached = PARTS_FORMATTER_CACHE.get(timeZone);
    if (cached)
        return cached;
    const f = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    });
    PARTS_FORMATTER_CACHE.set(timeZone, f);
    return f;
}
function localPartsAt(utc, timeZone) {
    const parts = getPartsFormatter(timeZone).formatToParts(utc);
    const get = (type) => {
        const part = parts.find((p) => p.type === type);
        if (!part)
            throw new Error(`Missing ${type} part for tz=${timeZone}`);
        return parseInt(part.value, 10);
    };
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        // Some Intl impls render "24" at midnight under hourCycle h23 — normalise.
        hour: get('hour') % 24,
        minute: get('minute'),
        second: get('second'),
    };
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
function pad4(n) {
    return n.toString().padStart(4, '0');
}
function localDateString(p) {
    return `${pad4(p.year)}-${pad2(p.month)}-${pad2(p.day)}`;
}
/**
 * Local-tz `YYYY-MM-DD` for an arbitrary UTC instant. The single source of
 * truth for "which local-tz day did this instant happen on" — consumers must
 * never `iso.slice(0, 10)`, which silently returns the UTC date.
 */
export function localDateInTz(utcIso, timeZone) {
    return localDateString(localPartsAt(new Date(utcIso), timeZone));
}
/**
 * UTC ms of local midnight (`YYYY-MM-DDT00:00:00` in `timeZone`).
 *
 * Used to slice a UTC span at local-day boundaries. DST-skipped or duplicated
 * hours land on the nearest valid UTC instant (acceptable — schedule data is
 * at most minute-granular and only a handful of hours per year sit on a DST
 * transition).
 */
export function localMidnightUtcMs(localDate, timeZone) {
    return localDateTimeToUtcMs(localDate, 0, 0, timeZone);
}
/**
 * UTC ms of an arbitrary local-clock instant `(date, hour, minute)` in
 * `timeZone`. Used wherever a user-facing local time picker (cross-day
 * window composers, shift editors, break-decision custom-window composers)
 * has to commit a real UTC instant — the conversion handles DST under the
 * same approximation as {@link localMidnightUtcMs}.
 */
export function localDateTimeToUtcMs(localDate, localHour, localMinute, timeZone) {
    const year = parseInt(localDate.substring(0, 4), 10);
    const month = parseInt(localDate.substring(5, 7), 10);
    const day = parseInt(localDate.substring(8, 10), 10);
    const guessMs = Date.UTC(year, month - 1, day, localHour, localMinute, 0);
    const local = localPartsAt(new Date(guessMs), timeZone);
    const localAsUtcMs = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    const offsetMs = localAsUtcMs - guessMs;
    return guessMs - offsetMs;
}
export function utcMsToLocalInstant(utcMs, timeZone) {
    const local = localPartsAt(new Date(utcMs), timeZone);
    return { date: localDateString(local), hour: local.hour, minute: local.minute };
}
/** Add one local-tz day to a `YYYY-MM-DD` (calendar math — no tz projection). */
function addDays(localDate, days) {
    const d = new Date(`${localDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().substring(0, 10);
}
/** Enumerate local-tz `YYYY-MM-DD` strings from `startDate` to `endDate` inclusive. */
export function enumerateLocalDates(startDate, endDate) {
    if (startDate > endDate)
        return [];
    const out = [];
    let cur = startDate;
    while (cur <= endDate) {
        out.push(cur);
        cur = addDays(cur, 1);
    }
    return out;
}
/**
 * Split a UTC span at local-tz midnight boundaries.
 *
 * Returns one entry per local-tz day the span touches. Each entry's
 * `[startsAt, endsAt]` is the slice of the original span that falls inside
 * that local-tz day; the first piece keeps the original `startsAt`, the last
 * piece keeps the original `endsAt`, and every interior boundary is local
 * midnight.
 *
 * A span that lies inside a single local-tz day round-trips to one entry
 * with the original instants. Zero-length and inverted spans (`end ≤ start`)
 * return `[]` — there is no meaningful split.
 */
export function splitWindowByLocalDays(startsAt, endsAt, timeZone) {
    const startMs = Date.parse(startsAt);
    const endMs = Date.parse(endsAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        throw new Error(`Invalid window: startsAt=${startsAt} endsAt=${endsAt}`);
    }
    if (endMs <= startMs)
        return [];
    const out = [];
    let cursorMs = startMs;
    let cursorIso = startsAt;
    let cursorDate = localDateInTz(startsAt, timeZone);
    while (cursorMs < endMs) {
        const nextDate = addDays(cursorDate, 1);
        const nextMidnightMs = localMidnightUtcMs(nextDate, timeZone);
        if (nextMidnightMs >= endMs) {
            // Span ends inside the current local-tz day.
            out.push({ localDate: cursorDate, startsAt: cursorIso, endsAt });
            break;
        }
        const boundaryIso = new Date(nextMidnightMs).toISOString();
        out.push({ localDate: cursorDate, startsAt: cursorIso, endsAt: boundaryIso });
        cursorMs = nextMidnightMs;
        cursorIso = boundaryIso;
        cursorDate = nextDate;
    }
    return out;
}
/**
 * UTC date range that fully covers `[startLocalDate, endLocalDate]` in
 * `timeZone`. Pads ±1 UTC day so any span whose UTC bucket sits just outside
 * the local-tz window (because of timezone offset) is still fetched. Use
 * when forwarding a user-facing local-tz range to a UTC-bucketed API.
 */
export function utcQueryRangeForLocalRange(startLocalDate, endLocalDate) {
    return {
        startDate: addDays(startLocalDate, -1),
        endDate: addDays(endLocalDate, 1),
    };
}
//# sourceMappingURL=local-tz-bucket.js.map